import usePageTitle from '@/hooks/use-page-title';
import { Link } from "react-router-dom";
import { ChevronRight, Cookie, Share2, Settings } from 'lucide-react';
import AprilPicto from '@/components/shared/april-picto';
import { PictoConformite, PictoTracking, PictoTop, PictoRapidite, PictoConseils } from '@/assets/icons-april-fr/pictos';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const cookiesData = [
    { editeur: 'Google', nom: 'APISID', finalite: 'Publicitaire', duree: '2 ans', type: 'Tiers' },
    { editeur: 'Google', nom: 'HSID', finalite: 'Publicitaire', duree: '2 ans', type: 'Tiers' },
    { editeur: 'Google', nom: 'IDE', finalite: 'Publicitaire', duree: '13 mois', type: 'Tiers' },
    { editeur: 'Google', nom: 'NID', finalite: 'Publicitaire', duree: '6 mois', type: 'Tiers' },
    { editeur: 'Google', nom: 'OTZ', finalite: 'Publicitaire', duree: '1 mois', type: 'Tiers' },
    { editeur: 'Google', nom: 'SAPISID', finalite: 'Publicitaire', duree: '2 ans', type: 'Tiers' },
    { editeur: 'Google', nom: 'SID', finalite: 'Publicitaire', duree: '2 ans', type: 'Tiers' },
    { editeur: 'Google', nom: 'SIDCC', finalite: 'Publicitaire', duree: '1 an', type: 'Tiers' },
    { editeur: 'Google', nom: 'SSID', finalite: 'Publicitaire', duree: '2 ans', type: 'Tiers' },
    { editeur: 'Google', nom: '__Secure-3PAPISID', finalite: 'Publicitaire', duree: '2 ans', type: 'Tiers' },
    { editeur: 'Google', nom: '__Secure-3PSID', finalite: 'Publicitaire', duree: '2 ans', type: 'Tiers' },
    { editeur: 'Google', nom: '__Secure-3PSIDCC', finalite: 'Publicitaire', duree: '1 an', type: 'Tiers' },
    { editeur: 'Facebook', nom: '_fbp', finalite: 'Publicitaire', duree: '3 mois', type: 'Tiers' },
    { editeur: 'Google', nom: '_ga', finalite: 'Audience', duree: '2 ans', type: 'Tiers' },
    { editeur: 'Google', nom: '_gcl_au', finalite: 'Publicitaire', duree: '3 mois', type: 'Tiers' },
    { editeur: 'April Moto', nom: '_gid', finalite: 'Audience', duree: '1 journée', type: 'Tiers' },
    { editeur: 'Matomo', nom: '_pk_id*', finalite: 'Audience', duree: '13 mois', type: 'Tiers' },
    { editeur: 'April Moto', nom: 'browser_uuid', finalite: 'Audience', duree: '12 mois', type: 'Interne' },
    { editeur: 'Facebook', nom: 'fr', finalite: 'Publicitaire', duree: '3 mois', type: 'Tiers' },
];

const getFinaliteBadge = (finalite: string) => {
    switch (finalite) {
        case 'Publicitaire':
            return <Badge variant="destructive" className="text-[10px] sm:text-xs">{finalite}</Badge>;
        case 'Audience':
            return <Badge variant="warning" className="text-[10px] sm:text-xs">{finalite}</Badge>;
        default:
            return <Badge variant="secondary" className="text-[10px] sm:text-xs">{finalite}</Badge>;
    }
};

const Cookies = () => {
    usePageTitle('Gestion des cookies');
    const showPopup = () => {
        const consent_cta = document.querySelectorAll<HTMLElement>(".consent-modal");
        if (consent_cta[0]) {
            consent_cta[0].style.display = 'block';
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="space-y-2 sm:space-y-3 mt-4">
                <nav className="flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground" aria-label="Fil d'Ariane">
                    <Link to="/" className="hover:text-foreground transition-colors no-underline">Mon espace</Link>
                    <ChevronRight className="w-3.5 h-3.5" />
                    <span>Gestion des cookies</span>
                </nav>
                <h1 className="text-xl sm:text-2xl font-bold">Gestion des cookies</h1>
            </div>

            {/* Préambule */}
            <Card>
                <CardHeader className="pb-6 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Cookie className="w-5 h-5 text-primary" />
                        Préambule
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                    <p className="text-sm">
                        La présente politique, qui complète les Conditions Générales d'Utilisation (CGU) et la Politique données personnelles du Site, précise quels sont les cookies et traceurs utilisés sur le Site ainsi que les moyens dont Vous disposez pour accepter ou refuser leur dépôt.
                    </p>
                    <p className="text-sm text-muted-foreground">
                        Les définitions figurant dans les Conditions Générales d'Utilisation du Site s'appliquent également à la présente Politique.
                    </p>
                    <Button variant="outline" size="sm" onClick={showPopup} className="cursor-pointer">
                        <Settings className="w-4 h-4" />
                        Gérer mes préférences
                    </Button>
                </CardContent>
            </Card>

            {/* Qu'est-ce qu'un cookie ? */}
            <Card>
                <CardHeader className="pb-6 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <AprilPicto icon={PictoConseils} size="md" />
                        Qu'est-ce qu'un cookie ?
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-3">
                    <p className="text-sm">
                        Un cookie est une suite d'informations, généralement de petite taille et identifié par un nom, qui peut être placé sur le terminal de l'internaute à l'occasion de la consultation d'un site internet ou d'une application et ce, grâce à son logiciel de navigation.
                    </p>
                    <p className="text-sm">
                        Un cookie permet à son émetteur d'identifier le terminal dans lequel il est enregistré, pendant la durée de validité ou d'enregistrement dudit cookie. Les cookies ont de multiples usages, certains sont indispensables à l'utilisation du site, alors que d'autres permettent par exemple d'optimiser et de personnaliser les contenus affichés ou de réaliser des statistiques.
                    </p>
                    <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                        <p className="text-sm font-medium">
                            Le terme cookie dans la présente Politique est entendu au sens large et couvre l'ensemble des traceurs déposés et/ou lus lors de la consultation du Site, quel que soit le type de terminal que Vous utilisez (ordinateur, smartphone, tablette numérique, etc.).
                        </p>
                    </div>
                </CardContent>
            </Card>

            {/* Cookies nécessaires - Tableau */}
            <Card>
                <CardHeader className="pb-6 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <AprilPicto icon={PictoConformite} size="md" />
                        Cookies nécessaires au fonctionnement du Site
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                    <p className="text-sm">
                        Il s'agit des cookies permettant au Site de fonctionner de manière optimale. Vous pouvez Vous opposer au dépôt de ces cookies et les supprimer en utilisant les paramètres de votre navigateur. Cependant, sans ces cookies Vous ne pourrez pas utiliser normalement le Site et votre expérience risque d'être dégradée.
                    </p>
                    <p className="text-sm text-muted-foreground">
                        Nous déclinons toute responsabilité pour les conséquences liées au fonctionnement dégradé des services du Site résultant de l'impossibilité d'enregistrer ou de consulter les cookies nécessaires à son fonctionnement que Vous auriez refusés ou supprimés.
                    </p>

                    {/* Table desktop */}
                    <div className="hidden sm:block overflow-x-auto rounded-lg border">
                        <table className="w-full text-sm" aria-label="Liste des cookies nécessaires au fonctionnement du site">
                            <thead>
                                <tr className="bg-muted/50 border-b">
                                    <th scope="col" className="text-left p-4 font-semibold">Editeur</th>
                                    <th scope="col" className="text-left p-4 font-semibold">Nom</th>
                                    <th scope="col" className="text-left p-4 font-semibold">Finalité</th>
                                    <th scope="col" className="text-left p-4 font-semibold">Durée</th>
                                    <th scope="col" className="text-left p-4 font-semibold">Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cookiesData.map((c, i) => (
                                    <tr key={i} className="border-b last:border-b-0 hover:bg-muted/30 transition-colors">
                                        <td className="p-4 text-muted-foreground">{c.editeur}</td>
                                        <td className="p-4 font-mono text-xs">{c.nom}</td>
                                        <td className="p-4">{getFinaliteBadge(c.finalite)}</td>
                                        <td className="p-4 text-muted-foreground">{c.duree}</td>
                                        <td className="p-4">
                                            <Badge variant={c.type === 'Interne' ? 'success' : 'secondary'} className="text-xs">{c.type}</Badge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Cards mobile */}
                    <div className="sm:hidden space-y-3">
                        {cookiesData.map((c, i) => (
                            <div key={i} className="p-4 bg-muted/50 rounded-lg space-y-2">
                                <div className="flex items-center justify-between">
                                    <p className="font-mono text-xs font-medium">{c.nom}</p>
                                    {getFinaliteBadge(c.finalite)}
                                </div>
                                <div className="flex items-center justify-between text-xs text-muted-foreground">
                                    <span>{c.editeur}</span>
                                    <span className="flex items-center gap-1"><AprilPicto icon={PictoRapidite} size="xs" />{c.duree}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Cookie exempt de consentement */}
            <Card>
                <CardHeader className="pb-6 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <AprilPicto icon={PictoTracking} size="md" />
                        Cookie exempt de consentement
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-3">
                    <p className="text-sm">
                        Le cookie analytique Realytics est un cookie de mesure d'audience et statistiques, de ce fait exempt de consentement selon la CNIL, utilisé pour mesurer la performance des campagnes TV des annonceurs sur le digital.
                    </p>
                    <p className="text-sm">
                        En plus de la mesure, Realytics offre des solutions d'optimisation ou de mesure d'audience avancée qui peuvent nécessiter de recueillir un consentement libre, explicite et non équivoque de l'utilisateur. Realytics devient alors un fournisseur de données pour les partenaires de l'annonceur, qui doit s'assurer de la conformité du partenaire opérant.
                    </p>
                    <p className="text-sm text-muted-foreground">
                        Dans les 2 cas, le cookie Realytics s'engage à respecter les données collectées et à ne collecter aucune donnée personnelle ou sensible et peut être désactivé depuis la page opt-out de Realytics.
                    </p>
                </CardContent>
            </Card>

            {/* Cookies soumis à votre consentement */}
            <Card>
                <CardHeader className="pb-6 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <AprilPicto icon={PictoConformite} size="md" />
                        Cookies soumis à votre consentement
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                    <p className="text-sm">
                        Il s'agit des cookies, y compris tiers, utilisés pour les finalités décrites ci-dessous. Vous pouvez accepter ou Vous opposer au dépôt de ces cookies en utilisant le module "Gestion cookies" présent sur le Site. Sans ces cookies Vous pourrez utiliser normalement le Site.
                    </p>
                    <p className="text-sm">
                        A partir des liens présents dans le module "Gestion cookies" présent sur le Site Vous pouvez également accéder aux politiques de confidentialité des éditeurs de cookies tiers utilisés sur le Site, et ainsi :
                    </p>
                    <ul className="space-y-2 text-sm ml-1">
                        <li className="flex items-start gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                            Consulter les informations relatives aux traitements que ces éditeurs réalisent pour leur propre compte et sous leur responsabilité à partir des données de navigation qu'ils collectent via les cookies qu'ils éditent.
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                            Obtenir leurs coordonnées afin de les contacter directement pour exercer vos droits sur les données qu'ils collectent.
                        </li>
                    </ul>

                    <h3 className="text-base font-semibold pt-2">Types de cookies utilisés</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="p-6 bg-muted/50 rounded-lg flex items-start gap-3">
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 shrink-0">
                                <Settings className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                                <p className="text-sm font-medium mb-1">Personnalisation de contenu</p>
                                <p className="text-xs text-muted-foreground">Permettent l'optimisation de l'ergonomie des contenus du Site.</p>
                            </div>
                        </div>
                        <div className="p-6 bg-muted/50 rounded-lg flex items-start gap-3">
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 shrink-0">
                                <AprilPicto icon={PictoTracking} size="sm" />
                            </div>
                            <div>
                                <p className="text-sm font-medium mb-1">Mesure d'audience</p>
                                <p className="text-xs text-muted-foreground">Permettent de générer des statistiques de fréquentation et d'utilisation du Site.</p>
                            </div>
                        </div>
                        <div className="p-6 bg-muted/50 rounded-lg flex items-start gap-3">
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 shrink-0">
                                <AprilPicto icon={PictoTop} size="sm" />
                            </div>
                            <div>
                                <p className="text-sm font-medium mb-1">Publicitaires</p>
                                <p className="text-xs text-muted-foreground">Permettent de proposer des publicités adaptées à vos centres d'intérêts.</p>
                            </div>
                        </div>
                        <div className="p-6 bg-muted/50 rounded-lg flex items-start gap-3">
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 shrink-0">
                                <Share2 className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                                <p className="text-sm font-medium mb-1">Partage réseaux sociaux</p>
                                <p className="text-xs text-muted-foreground">Permettent de faciliter le partage de contenu sur les réseaux sociaux.</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                        <AprilPicto icon={PictoRapidite} size="md" className="shrink-0" />
                        <div className="text-sm">
                            <p><strong>Durée de vie des cookies :</strong> 13 mois maximum</p>
                            <p className="text-muted-foreground"><strong>Conservation des données :</strong> 25 mois maximum</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Gestion des Cookies */}
            <Card>
                <CardHeader className="pb-6 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Settings className="w-5 h-5 text-primary" />
                        La gestion des Cookies
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-3">
                    <p className="text-sm">
                        Lors de votre visite sur le Site, une fenêtre Vous informe de la présence des cookies et Vous invite à indiquer vos choix soit de manière globale soit par type de cookie.
                    </p>
                    <p className="text-sm">
                        Vous pouvez à tout moment vous informer et paramétrer les cookies pour les accepter ou les refuser en Vous rendant dans le module "Gestion cookies" accessible sur chaque page du Site. Vous pouvez choisir vos préférences soit globalement pour le Site, soit par cookie.
                    </p>
                    <Button variant="outline" size="sm" onClick={showPopup} className="cursor-pointer">
                        <Settings className="w-4 h-4" />
                        Gérer mes préférences
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
};

export default Cookies;
